import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApp, toggleKey } from "@/context/AppContext";
import { useAdmin, AdminLoading } from "@/pages/admin/useAdmin";
import { StatusBadge } from "@/pages/admin/AdminOSList";
import { ServicePicker } from "@/components/site/ServicePicker";
import { VehicleDiagram } from "@/components/site/VehicleDiagram";
import { FileUploader } from "@/components/site/FileUploader";
import { SelectInput, TextArea, TextInput, Toggle } from "@/components/site/Field";
import { WhatsAppIcon } from "@/components/site/WhatsAppFloat";
import { api, brl, errMsg } from "@/lib/api";
import { PAGAMENTO, STATUS_LABEL, waLink } from "@/lib/content";

const TANQUE = ["Reserva", "1/4", "1/2", "3/4", "Cheio"];
const Box = ({ title, children, testid }) => (
  <div className="card-tech p-6" data-testid={testid}><p className="eyebrow mb-4">{title}</p>{children}</div>
);
const toForm = (o) => ({
  status: o.status, servicos: o.servicos, data_visita: o.data_visita || "", data_servico: o.data_servico || "", data_pretendida: o.data_pretendida || "",
  forma_pagamento: o.forma_pagamento, fora_municipio: o.fora_municipio, observacoes_prestador: o.observacoes_prestador || "",
  deslocamento: o.valores.deslocamento ?? "", desconto: o.valores.desconto ?? 0, sinal_percent: o.valores.sinal_percent ?? 0,
  inspecao: o.inspecao || {}, marcacoes: o.marcacoes || [], fotos: o.fotos_meta || [],
});

const Valores = ({ v }) => (
  <div className="mt-4 space-y-1 border-t border-slate-800 pt-4 text-sm" data-testid="admin-os-valores">
    {[["Material", v.material], ["Mão de obra", v.mao_de_obra], ["Custo do serviço", v.custo_servico], ["Deslocamento", v.deslocamento], ["Desconto", v.desconto], ["Sinal", v.sinal], ["Saldo", v.saldo]].map(([k, x]) => (
      <div key={k} className="flex justify-between text-slate-400"><span>{k}</span><span className="font-mono text-slate-200">{x == null ? "A definir" : brl(x)}</span></div>
    ))}
    <div className="flex justify-between pt-2 font-heading text-xl font-bold text-white"><span>Total</span><span className="text-red-500" data-testid="admin-os-total">{brl(v.total)}</span></div>
    {!v.precificado && <p className="text-xs text-amber-500">Defina preços de todos os itens (aba Preços) e o deslocamento para liberar o aceite. Ao salvar, a tabela de preços atual é aplicada.</p>}
  </div>
);

export default function AdminOSEdit() {
  const user = useAdmin();
  const { id } = useParams();
  const { config } = useApp();
  const [os, setOs] = useState(null);
  const [f, setF] = useState(null);
  const [tipo, setTipo] = useState("encontrado");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) api.get(`/admin/os/${id}`).then((r) => { setOs(r.data); setF(toForm(r.data)); });
  }, [user, id]);
  if (!user || !os || !f || !config) return <AdminLoading />;

  const locked = !!os.aceite;
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const setIns = (k) => (v) => setF((s) => ({ ...s, inspecao: { ...s.inspecao, [k]: v } }));
  const link = `${window.location.origin}/os/${os.share_token}`;
  const clientWa = `55${(os.cliente.whatsapp || os.cliente.telefone).replace(/\D/g, "").replace(/^55/, "")}`;

  const save = async (extra = {}) => {
    setBusy(true);
    const payload = {
      data_visita: f.data_visita, data_servico: f.data_servico, data_pretendida: f.data_pretendida, observacoes_prestador: f.observacoes_prestador,
      inspecao: f.inspecao, marcacoes: f.marcacoes, fotos: f.fotos.map((x) => x.id), ...extra,
    };
    if (f.status !== os.status) payload.status = f.status;
    if (!locked) {
      Object.assign(payload, { recalcular_precos: true, forma_pagamento: f.forma_pagamento, fora_municipio: f.fora_municipio, desconto: Number(f.desconto) || 0, sinal_percent: Number(f.sinal_percent) || 0 });
      if (f.deslocamento === "") payload.limpar_deslocamento = true; else payload.deslocamento = Number(f.deslocamento);
      if (JSON.stringify(f.servicos) !== JSON.stringify(os.servicos)) payload.servicos = f.servicos;
    }
    try {
      const { data } = await api.put(`/admin/os/${id}`, payload);
      setOs(data); setF(toForm(data));
      toast.success("Ordem de serviço salva");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="admin-os-edit">
      <Link to="/admin" className="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase text-slate-400 hover:text-white" data-testid="admin-os-back"><ArrowLeft className="h-4 w-4" /> Voltar ao painel</Link>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-3xl font-bold text-red-500" data-testid="admin-os-numero">{os.numero}</p>
          <p className="text-slate-300">{os.cliente.nome} · {os.veiculo.marca} {os.veiculo.modelo} · {os.cliente.cidade}/{os.cliente.estado}</p>
          <div className="mt-2"><StatusBadge s={os.status} /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={link} target="_blank" rel="noreferrer" className="btn-line !py-2" data-testid="admin-os-open-public"><ExternalLink className="h-4 w-4" /> Ver OS</a>
          <a href={waLink(`Olá, ${os.cliente.nome}! Segue a sua ordem de serviço ${os.numero} da Nillo Ruídos: ${link}`, clientWa)} target="_blank" rel="noreferrer" className="btn-line !py-2" data-testid="admin-os-wa-client"><WhatsAppIcon className="h-4 w-4" /> Enviar ao cliente</a>
          <button onClick={() => save()} disabled={busy} className="btn-red !py-2" data-testid="admin-os-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar</button>
        </div>
      </div>
      {locked && <p className="mb-6 border-l-2 border-green-600 bg-green-950/30 p-3 text-sm text-green-300" data-testid="admin-os-locked">Aceita por {os.aceite.nome} em {new Date(os.aceite.data_hora).toLocaleString("pt-BR")} (IP {os.aceite.ip || "n/d"}). Dados comerciais bloqueados.</p>}
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          <Box title="Serviços selecionados" testid="admin-os-services">
            <ServicePicker items={config.items} groups={config.groups} selected={f.servicos} onToggle={(k) => set("servicos")(toggleKey(f.servicos, k))} disabled={locked} />
          </Box>
          <Box title="Condições do veículo (inspeção inicial)" testid="admin-os-inspecao">
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectInput label="Nível do tanque de combustível" value={f.inspecao.tanque} onChange={setIns("tanque")} options={TANQUE} testid="ins-tanque" />
              <TextInput label="Condições dos pneus" value={f.inspecao.pneus} onChange={setIns("pneus")} testid="ins-pneus" />
              <TextInput label="Condições dos bancos" value={f.inspecao.bancos} onChange={setIns("bancos")} testid="ins-bancos" />
              <TextInput label="Estado do interior" value={f.inspecao.interior} onChange={setIns("interior")} testid="ins-interior" />
              <TextInput label="Estado externo" value={f.inspecao.externo} onChange={setIns("externo")} testid="ins-externo" className="sm:col-span-2" />
              <TextArea label="Danos previamente declarados" value={f.inspecao.danos_declarados} onChange={setIns("danos_declarados")} testid="ins-danos-declarados" />
              <TextArea label="Danos encontrados na inspeção" value={f.inspecao.danos_encontrados} onChange={setIns("danos_encontrados")} testid="ins-danos-encontrados" />
            </div>
            <p className="mt-6 border-l-2 border-red-600 pl-3 text-xs text-slate-400">Registro fotográfico obrigatório: adicione fotos ou marque os danos no diagrama antes de iniciar a execução.</p>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div>
                <div className="mb-3 grid grid-cols-2 gap-2">
                  {["declarado", "encontrado"].map((t) => (
                    <button key={t} type="button" onClick={() => setTipo(t)} data-testid={`diagram-tipo-${t}`} className={`border px-2 py-1.5 text-xs uppercase ${tipo === t ? "border-red-600 bg-red-600 text-white" : "border-slate-700 text-slate-400"}`}>{t === "declarado" ? "Dano declarado" : "Dano encontrado"}</button>
                  ))}
                </div>
                <VehicleDiagram marks={f.marcacoes} onChange={set("marcacoes")} tipo={tipo} />
                <p className="mt-2 text-center text-xs text-slate-500">Clique no diagrama para marcar.</p>
              </div>
              <div className="space-y-2">
                {f.marcacoes.map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-6 font-mono text-xs text-red-400">{i + 1}.</span>
                    <input className="field !py-1.5" placeholder={m.tipo === "declarado" ? "Dano declarado" : "Dano encontrado"} value={m.nota} data-testid={`mark-note-${i}`}
                      onChange={(e) => set("marcacoes")(f.marcacoes.map((x, k) => (k === i ? { ...x, nota: e.target.value } : x)))} />
                    <button type="button" onClick={() => set("marcacoes")(f.marcacoes.filter((_, k) => k !== i))} className="p-1 text-slate-500 hover:text-red-400" data-testid={`mark-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <FileUploader label="Registro fotográfico" accept="image/*" camera files={f.fotos} onChange={set("fotos")} testid="os-photos-upload" />
              </div>
            </div>
          </Box>
          <Box title="Observações do prestador">
            <TextArea label="Observações" rows={4} value={f.observacoes_prestador} onChange={set("observacoes_prestador")} testid="admin-os-obs" />
            {os.observacoes && <p className="mt-3 text-sm text-slate-400"><b className="text-slate-200">Cliente:</b> {os.observacoes}</p>}
          </Box>
        </div>
        <div className="space-y-6">
          <Box title="Status e datas" testid="admin-os-status-box">
            <div className="space-y-4">
              <Selectish value={f.status} onChange={set("status")} />
              <TextInput label="Data da visita técnica" type="date" value={f.data_visita} onChange={set("data_visita")} testid="admin-os-data-visita" />
              <TextInput label="Data da prestação do serviço" type="date" value={f.data_servico} onChange={set("data_servico")} testid="admin-os-data-servico" />
              <TextInput label="Data pretendida (cliente)" type="date" value={f.data_pretendida} onChange={set("data_pretendida")} testid="admin-os-data-pretendida" />
            </div>
          </Box>
          <Box title="Precificação" testid="admin-os-pricing">
            <div className="space-y-4">
              <TextInput label="Custo estimado de deslocamento (R$)" type="number" min="0" step="0.01" value={f.deslocamento} onChange={set("deslocamento")} testid="admin-os-deslocamento" disabled={locked} />
              <TextInput label="Desconto (R$)" type="number" min="0" step="0.01" value={f.desconto} onChange={set("desconto")} testid="admin-os-desconto" disabled={locked} />
              <Toggle label="Fora do município do Rio de Janeiro" checked={f.fora_municipio} onChange={(x) => setF((s) => ({ ...s, fora_municipio: x, sinal_percent: x ? 20 : s.sinal_percent }))} testid="admin-os-fora" />
              <TextInput label="Sinal (%)" type="number" min="0" max="100" value={f.sinal_percent} onChange={set("sinal_percent")} testid="admin-os-sinal" disabled={locked} />
              <SelectInput label="Forma de pagamento" value={f.forma_pagamento} onChange={set("forma_pagamento")} options={PAGAMENTO.opcoes} testid="admin-os-pagamento" />
            </div>
            <Valores v={os.valores} />
          </Box>
        </div>
      </div>
    </section>
  );
}

const Selectish = ({ value, onChange }) => (
  <label className="block">
    <span className="field-label">Status</span>
    <select className="field" value={value} onChange={(e) => onChange(e.target.value)} data-testid="admin-os-status">
      {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  </label>
);
